import { useEffect, useState } from "react";

import { newULID } from "@/lib/api/client";
import {
  useCreatePendingAction,
  useUpdatePendingAction,
} from "@/lib/api/pending-actions";
import type { Grow, PendingAction, Urgency } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorState } from "@/components/error-state";

const URGENCIES: Urgency[] = ["HIGH", "MED", "ROUTINE", "PARKED"];

// Shared inline form for manual create + edit of a pending action. There is no
// modal/dialog system in this app, so this renders inline (toggled open by the
// caller) and matches the inline-form style used elsewhere. snake_case DTOs.

// NewActionForm — manual create. Lets the grower pick a grow (prefilled to the
// active filter grow when one is selected), title, urgency and rationale.
export function NewActionForm({
  grows,
  defaultGrowID,
  onDone,
}: {
  grows: Grow[];
  defaultGrowID?: string;
  onDone: () => void;
}) {
  const create = useCreatePendingAction();
  const active = grows.filter((g) => !g.archived_at);
  const firstActiveID = active[0]?.id;
  const [growID, setGrowID] = useState<string>(
    defaultGrowID && active.some((g) => g.id === defaultGrowID)
      ? defaultGrowID
      : (firstActiveID ?? ""),
  );
  // If the form mounted before the grows list loaded, the initializer above
  // picked "" — backfill a default once grows arrive so the Grow select isn't
  // stuck blank (which would leave "Add action" permanently disabled).
  useEffect(() => {
    if (growID || !firstActiveID) return;
    setGrowID(
      defaultGrowID && active.some((g) => g.id === defaultGrowID)
        ? defaultGrowID
        : firstActiveID,
    );
  }, [growID, firstActiveID, defaultGrowID, active]);
  const [title, setTitle] = useState("");
  const [urgency, setUrgency] = useState<Urgency>("MED");
  const [rationale, setRationale] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!growID || !title.trim()) return;
    create.mutate(
      {
        id: newULID(),
        grow_id: growID,
        title: title.trim(),
        urgency,
        rationale: rationale.trim() || undefined,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-md border bg-muted/20 p-4"
      aria-label="New pending action"
      noValidate
    >
      {create.error && (
        <ErrorState error={create.error} retry={() => create.reset()} />
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="pa-new-grow">Grow</Label>
          <Select value={growID} onValueChange={setGrowID}>
            <SelectTrigger id="pa-new-grow" className="w-full">
              <SelectValue placeholder="Pick a grow…" />
            </SelectTrigger>
            <SelectContent>
              {active.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.species} — {g.system_type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pa-new-urgency">Urgency</Label>
          <Select
            value={urgency}
            onValueChange={(v) => setUrgency(v as Urgency)}
          >
            <SelectTrigger id="pa-new-urgency" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {URGENCIES.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="pa-new-title">Title</Label>
        <Input
          id="pa-new-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Lower pH by 0.3"
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="pa-new-rationale">Rationale</Label>
        <Textarea
          id="pa-new-rationale"
          rows={2}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="Optional — why this matters."
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={create.isPending || !growID || !title.trim()}
        >
          {create.isPending ? "Adding…" : "Add action"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// EditActionForm — inline edit of an existing row. Submits via
// useUpdatePendingAction with expected_updated_at from the row for the
// optimistic-lock contract.
export function EditActionForm({
  row,
  onDone,
}: {
  row: PendingAction;
  onDone: () => void;
}) {
  const update = useUpdatePendingAction(row.id, row.grow_id);
  const [title, setTitle] = useState(row.title);
  const [urgency, setUrgency] = useState<Urgency>(row.urgency);
  const [rationale, setRationale] = useState(row.rationale ?? "");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    update.mutate(
      {
        expected_updated_at: row.updated_at,
        title: title.trim(),
        urgency,
        rationale: rationale.trim() || undefined,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-md border bg-muted/20 p-3"
      aria-label={`Edit ${row.title}`}
      noValidate
    >
      {update.error && (
        <ErrorState error={update.error} retry={() => update.reset()} />
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`pa-edit-title-${row.id}`}>Title</Label>
          <Input
            id={`pa-edit-title-${row.id}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`pa-edit-urgency-${row.id}`}>Urgency</Label>
          <Select
            value={urgency}
            onValueChange={(v) => setUrgency(v as Urgency)}
          >
            <SelectTrigger id={`pa-edit-urgency-${row.id}`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {URGENCIES.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`pa-edit-rationale-${row.id}`}>Rationale</Label>
        <Textarea
          id={`pa-edit-rationale-${row.id}`}
          rows={2}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={update.isPending || !title.trim()}
        >
          {update.isPending ? "Saving…" : "Save"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
